/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  Box,
  Chip,
  CircularProgress,
  Link,
  Portal,
  styled,
} from '@material-ui/core';
import {
  useGetConversionConfigQuery,
  useReportConvertionMutation,
} from '@src/reducers/capiAPI';
import { updateChatState } from '@src/reducers/chats';
import { chatViewerActions } from '@src/reducers/chatViewer';
import usePopoverState from '@src/utils/usePopoverState';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import ReportCAPIEventPopover, {
  FormValues,
  ReportedCAPIEventSnackbar,
} from '../CAPI/ReportCAPIEventPopover';

type ChatViewerReportCAPIEventProps = {
  module: 'AUDIT' | 'CONSOLE';
  chatId?: string;
  currentCAPIEvents?: string[];
};
const ChatViewerReportCAPIEvent: React.FC<ChatViewerReportCAPIEventProps> = ({
  module,
  chatId = '',
  currentCAPIEvents = [],
}) => {
  const popover = usePopoverState();
  const ref = useRef<HTMLDivElement>(null);
  const capiConfig = useGetConversionConfigQuery();
  const config = capiConfig.data?.manual;
  const dispatch = useDispatch();
  // eslint-disable-next-line no-param-reassign

  const onSubmitHandler = useCallback(
    (values: FormValues) => {
      const conversionTypes =
        values.event === 'PURCHASE' ? ['LEAD', 'PURCHASE'] : ['LEAD'];

      dispatch(chatViewerActions.updateChatDetails({ conversionTypes }));
      dispatch(updateChatState({ id: chatId, conversionTypes }));
    },
    [dispatch]
  );

  const [, reportEventResult] = useReportConvertionMutation({
    fixedCacheKey: chatId,
  });

  useEffect(() => {
    if (reportEventResult.isError) {
      dispatch(
        chatViewerActions.updateChatDetails({ conversionTypes: undefined })
      );
      dispatch(updateChatState({ id: chatId, conversionTypes: undefined }));
    }
  }, [reportEventResult.isError]);

  const [capiEventSent, setCAPIEventSent] = useState('');

  const disabled = !config?.modules?.includes(module);
  if (disabled) return null;

  return (
    <>
      <div ref={ref}>
        <Box display="flex" alignItems="center" gridGap={8}>
          <strong>
            <FormattedMessage id="CAPI.CAPIEvents" />:
          </strong>
          {currentCAPIEvents?.map(event => (
            <CAPIChip
              key={event}
              className={event}
              label={
                <FormattedMessage key={event} id={`CAPI.Event.${event}`} />
              }
            />
          ))}
          {currentCAPIEvents.length < 2 && (
            <>
              <Link
                component="button"
                style={{ fontWeight: '600' }}
                onClick={() => popover.open(ref.current)}
                disabled={reportEventResult.isLoading}
              >
                <Box display="flex" alignItems="center" gridGap={8}>
                  {reportEventResult.isLoading ? (
                    <>
                      <FormattedMessage id="CAPI.Reporting" />
                      <CircularProgress size={12} thickness={4} />
                    </>
                  ) : (
                    <>
                      {currentCAPIEvents.length === 0 && (
                        <FormattedMessage id="CAPI.ReportCAPIEvent" />
                      )}
                      {currentCAPIEvents.includes('LEAD') && (
                        <FormattedMessage id="CAPI.ReportPurchase" />
                      )}
                    </>
                  )}
                </Box>
              </Link>
              <ReportCAPIEventPopover
                key={chatId}
                initialEvent={
                  currentCAPIEvents.includes('LEAD') ? 'PURCHASE' : undefined
                }
                eventFieldReadOnly={currentCAPIEvents.includes('LEAD')}
                chatId={chatId}
                open={popover.isOpen}
                onClose={popover.close}
                anchorEl={popover.anchorEl}
                module={module}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                onSubmit={onSubmitHandler}
                onSuccess={values => {
                  setCAPIEventSent(values.event);
                }}
              />
            </>
          )}
        </Box>
      </div>
      <Portal container={document.getElementById('snackbar-subcontainer')}>
        <ReportedCAPIEventSnackbar
          event={capiEventSent}
          onClose={() => setCAPIEventSent('')}
        />
      </Portal>
    </>
  );
};

export default ChatViewerReportCAPIEvent;

const CAPIChip = styled(Chip)({
  border: '1px solid currentColor',
  padding: '0',
  height: 18,
  fontWeight: 500,
  '&.LEAD': {
    backgroundColor: '#FDE5C6',
    color: '#F8981D',
  },
  '&.PURCHASE': {
    backgroundColor: '#D1E3F2',
    color: '#478ECC',
  },
});
